import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { useUser } from '../../contexts/UserContext';

/**
 * Quick-login buttons for local dev. Visible only when Vite's `import.meta.env.DEV`
 * is true — production builds tree-shake this block to nothing.
 *
 * The accounts must already exist. Seed them with:
 *
 *   docker exec marketing_portal-api-1 python -m api.scripts.seed_dev_users
 *
 * Password is `demo-portal-2026` for all three accounts (same as seed_v1_demo).
 */
const DEV_USERS = [
  {
    email: 'demo-admin@demo.example.com',
    title: 'Admin',
    blurb: 'Super admin · sees every client',
    tone: 'primary' as const,
    initials: 'AD',
  },
  {
    email: 'demo-user-1@demo.example.com',
    title: 'Owner 1',
    blurb: 'Agency owner · one client',
    tone: 'dark' as const,
    initials: 'O1',
  },
  {
    email: 'demo-user-2@demo.example.com',
    title: 'Owner 2',
    blurb: 'Agency owner · different client',
    tone: 'muted' as const,
    initials: 'O2',
  },
];

const DEV_PASSWORD = 'demo-portal-2026';

const TONE_CLASSES: Record<'primary' | 'dark' | 'muted', string> = {
  primary: 'bg-[var(--primary)] text-white',
  dark: 'bg-zinc-800 text-white',
  muted: 'bg-zinc-200 text-zinc-700',
};

const DevDemoLogins: React.FC = () => {
  const navigate = useNavigate();
  const { reloadUser } = useUser();
  const [pending, setPending] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Vite inlines this — production bundle drops the whole component body.
  if (!import.meta.env.DEV) return null;

  const click = async (email: string) => {
    setPending(email);
    setErr(null);
    try {
      const response = await authService.login({ email, password: DEV_PASSWORD });
      if ('requires_2fa' in response && response.requires_2fa) {
        setErr(`${email}: 2FA is enabled — finish via the regular form`);
        setPending(null);
        return;
      }
      await reloadUser();
      navigate('/app');
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } }).response?.data?.detail
        ?? (e as { message?: string }).message
        ?? 'Login failed';
      setErr(
        /invalid (email|password|credentials)/i.test(msg)
          ? `${email} not seeded — run scripts.seed_dev_users`
          : `${email}: ${msg}`
      );
      setPending(null);
    }
  };

  return (
    <div className="mt-7 space-y-3">
      <div className="flex items-center gap-3">
        <span className="text-[0.62rem] font-bold uppercase tracking-[0.18em] text-[var(--text-muted)]">
          Dev quick-login
        </span>
        <span className="flex-1 h-px bg-[var(--border)]" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
        {DEV_USERS.map((u) => (
          <button
            key={u.email}
            type="button"
            disabled={pending !== null}
            onClick={() => click(u.email)}
            className="group text-left rounded-lg bg-white border border-[var(--border)] hover:border-[var(--accent)] hover:shadow-md hover:-translate-y-0.5 transition-all p-3 disabled:opacity-50 disabled:cursor-wait disabled:hover:translate-y-0 disabled:hover:shadow-none"
            title={u.email}
          >
            <div className="flex items-start gap-2.5">
              <span
                className={`shrink-0 w-9 h-9 rounded flex items-center justify-center font-bold text-xs uppercase tracking-tight ${TONE_CLASSES[u.tone]}`}
              >
                {u.initials}
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-[0.78rem] uppercase tracking-[0.06em] text-[var(--text)] group-hover:text-[var(--accent)] transition-colors">
                  {pending === u.email ? 'Signing in…' : u.title}
                </div>
                <div className="text-[0.65rem] text-[var(--text-muted)] leading-snug">
                  {u.blurb}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
      {err && (
        <p className="text-xs text-red-700">{err}</p>
      )}
      <p className="text-[0.62rem] text-[var(--text-muted)]/70 text-center">
        Dev-only (Vite <code className="font-mono">DEV</code> flag). Seed:{' '}
        <code className="font-mono">
          docker exec marketing_portal-api-1 python -m api.scripts.seed_dev_users
        </code>
      </p>
    </div>
  );
};

export default DevDemoLogins;
